import defaultAxiosClient from '../../plugins/axios';
import {toYear, toYearAndMonth} from "@/util/DateUtil";

const initialState = {
    users: [],
    selectedUser: {},
    selectedUserDetail: {},
    usersMinimalInfo: [],
    recupWithDeltas: {},
    remainingVacationDays: 0,
    vacationTypes: [],
    userAbsences: [],
    vacationCounters: [],
    userAbsenceCounters: [],
    usedYear: new Date(),
    previousYearAvailable: false,
    nextYearAvailable: false,
    currentBalance: 0,
    originalBalance: 0,
    hrEmployers: [],
    recupForYearDto: [],
    recupBonuses: [],
    employmentEndDate: null,
    prolongedSickLeaveStartDate: undefined,
    prolongedSickLeaveEndDate: undefined,
    selectedUserTerminationDate: undefined
};

const user = {
    namespaced: true,
    state() {
        return {...initialState};
    },
    getters: {
        users: state => state.users,
        selectedUserDetail: state => state.selectedUserDetail,
        usersMinimalInfo: state => state.usersMinimalInfo,
        recupWithDeltas: state => state.recupWithDeltas,
        remainingVacationDays: state => state.remainingVacationDays,
        vacationTypes: state => state.vacationTypes,
        userAbsences: state => state.userAbsences,
        vacationCounters: state => state.vacationCounters,
        userAbsenceCounters: state => state.userAbsenceCounters,
        usedYear: state => state.usedYear,
        previousYearAvailable: state => state.previousYearAvailable,
        nextYearAvailable: state => state.nextYearAvailable,
        currentBalance: state => state.currentBalance,
        originalBalance: state => state.originalBalance,
        hrEmployers: state => state.hrEmployers,
        prolongedSickLeaveStartDate: state => state.prolongedSickLeaveStartDate,
        prolongedSickLeaveEndDate: state => state.prolongedSickLeaveEndDate,
        recupForYearDto: state => state.recupForYearDto,
        recupBonuses: state => state.recupBonuses,
        selectedUserTerminationDate: state => state.selectedUserTerminationDate
    },
    mutations: {
        resetState(state) {
            Object.assign(state, initialState);
        },
        setUsers: (state, userOverviewDtoList) => {
            state.users = userOverviewDtoList;
        },
        setSelectedUser(state, payload) {
            state.selectedUser = payload;
        },
        setSelectedUserDetail(state, payload) {
            state.selectedUserDetail = payload;
        },
        addUserToList(state, payload) {
            const newUser = {
                id: payload.id,
                employeeNr: payload.employeeNr,
                email: payload.email,
                firstName: payload.firstName,
                lastName: payload.lastName,
                partTimePercentage: payload.partTimePercentage,
                recup: 0,
                sickDayHours: payload.sickDayHours,
                employmentEndDate: payload.employmentEndDate
            };
            state.users = [...state.users, newUser];
        },
        addContractToSelectedUserDetail(state, payload) {
            const contract = {
                id: payload.id,
                customerId: payload.customerId,
                customerName: payload.customerName,
                contractPeriod: payload.contractPeriod
            };
            state.selectedUserDetail.contractSummaryDtos = [...state.selectedUserDetail.contractSummaryDtos, contract];
        },
        setUsersMinimalInfo(state, payload) {
            const dataToObjects = [];
            payload.forEach(user => {
                dataToObjects.push({
                    id: user.id,
                    email: user.email,
                });
            });
            state.usersMinimalInfo = dataToObjects;
        },
        updateSelectedUserDetail(state, payload) {
            if (state.selectedUserDetail.id === payload.id) {
                state.selectedUserDetail = {
                    ...state.selectedUserDetail,
                    ...payload
                };
            } else {
                state.selectedUserDetail = {};
            }
        },
        updateRemainingVacationDays(state, payload) {
            state.remainingVacationDays = payload.remainingVacationDays;
        },
        setRecupWithDeltas(state, payload) {
            state.recupWithDeltas = payload;
        },
        setRemainingVacationDays(state, payload) {
            state.remainingVacationDays = payload.totalVacationDays - payload.plannedVacationDays;
        },
        updateRecupInSelectedUserDetail(state, payload) {
            state.selectedUserDetail = {
                ...state.selectedUserDetail,
                recup: payload.hours,
                recupConfirmedYearMonth: payload.yearMonth,
            };
        },
        setUserAbsences(state, payload) {
            state.userAbsences = payload;
        },
        updateContractInSelectedUserDetail(state, updatedContract) {
            const indexOfUpdatedContract = state.selectedUserDetail.contractSummaryDtos.findIndex(contract => contract.id === updatedContract.id);
            if (indexOfUpdatedContract !== -1) {
                state.selectedUserDetail.contractSummaryDtos[indexOfUpdatedContract] = updatedContract;
                state.selectedUserDetail.contractSummaryDtos = [...state.selectedUserDetail.contractSummaryDtos];
            }
        },
        setUserOnProlongedSickLeave(state, payload) {
            state.selectedUserDetail.onProlongedSickLeave = payload.onProlongedSickLeave;
        },
        setVacationTypes(state, payload) {
            state.vacationTypes = payload.vacationCounters;
        },
        setVacationCounters(state, payload) {
            state.vacationCounters = payload.allUsersWithVacationCounters;
        },
        setUserAbsenceCounters(state, payload) {
            state.userAbsenceCounters = payload.absenceCounters;
        },
        updateUsedYear(state, year) {
            state.usedYear = new Date().setFullYear(year);
        },
        setPreviousYearAvailable(state, payload) {
            state.previousYearAvailable = payload.previousYearAvailable;
        },
        setNextYearAvailable(state, payload) {
            state.nextYearAvailable = payload.nextYearAvailable;
        },
        setCurrentBalance(state, payload) {
            state.currentBalance = payload;
        },
        setOriginalBalance(state, payload) {
            state.originalBalance = payload;
        },
        setHREmployers(state, payload) {
            state.hrEmployers = payload;
        },
        setRecupForYearDto(state, payload) {
            state.recupForYearDto = payload;
        },
        setRecupBonusDtos(state, recupbonuses) {
            state.recupBonuses = recupbonuses;
        }
    },
    actions: {
        fetchUsers({commit}) {
            return defaultAxiosClient.get(`/administration/user`)
                .then(response => {
                    commit('setUsers', response.data._embedded?.userOverviewDtoList);
                    commit('setUsersMinimalInfo', response.data._embedded?.userOverviewDtoList);
                })
                .catch(() => commit('resetState'));
        },
        fetchActiveUsers({commit}) {
            return defaultAxiosClient.get(`/administration/user/active`)
                .then(response => {
                    commit('setUsers', response.data._embedded?.userOverviewDtoList);
                    commit('setUsersMinimalInfo', response.data._embedded?.userOverviewDtoList);
                })
                .catch(() => commit('resetState'));
        },
        fetchDetailsForSelectedUser({commit, state}) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.get(`/administration/user/${userId}`)
                .then(response => commit('setSelectedUserDetail', response.data))
                .catch(() => commit('resetState'));
        }
        ,
        fetchDetailsForSelectedUserByUserId({commit}, userId) {
            return defaultAxiosClient.get(`/administration/user/${userId}`)
                .then(response => commit('setSelectedUserDetail', response.data))
                .catch(() => commit('resetState'));
        }
        ,
        createUser({commit}, createUserDto) {
            return defaultAxiosClient.post("/administration/user", createUserDto)
                .then(response => commit('addUserToList', response.data));
        }
        ,
        createRecupBonus({state, commit}, createRecupBonusDto) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.post(`/administration/recup/${userId}`, createRecupBonusDto).then(response => {
                const recupForMonthDtos = response.data.recupForMonthDtos;
                const recupBonuses = response.data.recupBonusDtos;
                commit('setRecupForYearDto', recupForMonthDtos);
                commit('setRecupBonusDtos', recupBonuses);
            });
        }
        ,
        deleteRecupBonus({state, commit}, date) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.delete(`/administration/recup/${userId}/${date}`).then(response => {
                const recupForMonthDtos = response.data.recupForMonthDtos;
                const recupBonuses = response.data.recupBonusDtos;
                commit('setRecupForYearDto', recupForMonthDtos);
                commit('setRecupBonusDtos', recupBonuses);
            });
        }
        ,
        updateUser({commit, state}, updateUserDto) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.put(`/administration/user/${userId}`, updateUserDto)
                .then(response => commit('updateSelectedUserDetail', response.data));
        }
        ,
        fetchVacationForSelectedUserAndCurrentYear({commit, state}) {
            const userId = state.selectedUser.id;
            const currentYear = new Date().getFullYear().toString();
            return defaultAxiosClient.get(`/administration/user/${userId}/vacation/${currentYear}`)
                .then(response => {
                    commit('setRemainingVacationDays', response.data);
                    commit('setVacationTypes', response.data);
                })
                .catch(() => commit('resetState'));
        }
        ,
        fetchVacationBalanceForAllUsersSpecificYear({commit}, year) {
            return defaultAxiosClient.get(`/administration/user/vacationcounters/${year}`)
                .then(response => {
                    commit('setVacationCounters', response.data);
                    commit('setPreviousYearAvailable', response.data);
                    commit('setNextYearAvailable', response.data);
                })
                .catch(() => commit('resetState'));
        }
        ,
        fetchAbsenceCountersForUser({commit, state}, year) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.get(`/administration/user/${userId}/absencecounters/${year}`)
                .then(response => {
                    commit('setUserAbsenceCounters', response.data);
                })
                .catch(() => commit('resetState'));
        }
        ,
        fetchHREmployers({commit}) {
            return defaultAxiosClient.get(`/administration/user/hremployers`)
                .then(response => commit('setHREmployers', response.data.allHREmployers))
                .catch(() => commit('resetState'));
        }
        ,
        updateVacationCounterBalances({commit}, {year, userId, updateVacationCounterDto}) {
            return defaultAxiosClient.put(`/administration/user/vacationcounter/update/${userId}/${year}`, updateVacationCounterDto)
                .then(response => commit('setVacationCounters', response.data))
                .catch(() => commit('resetState'));
        }
        ,
        confirmRecupForSelectedUser({commit, state}, confirmRecupDto) {
            const userId = state.selectedUserDetail.id;
            return defaultAxiosClient.post(`/administration/user/${userId}/recup`, confirmRecupDto)
                .then(response => {
                    commit('setRecupWithDeltas', response.data);
                    commit('updateRecupInSelectedUserDetail', response.data);
                });
        }
        ,
        updateLastRecupForSelectedUser({commit, state}, updateLastRecupDto) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.patch(`/administration/user/${userId}/recup/updatelast`, updateLastRecupDto)
                .then(response => {
                    commit('setRecupWithDeltas', response.data);
                    commit('updateRecupInSelectedUserDetail', response.data);
                });
        }
        ,
        fetchUserAbsences({commit}) {
            const yearMonth = toYearAndMonth(this.state.time.currentlySelectedMonth);

            return defaultAxiosClient.get(`/administration/timesheet/${yearMonth}/absences`)
                .then(response => commit('setUserAbsences', response.data._embedded?.userAbsenceDtoList || []));
        }
        ,
        endProlongedSickLeaveForSelectedUser({commit, state}, endDate) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.put(`/administration/user/${userId}/onProlongedSickLeave/end`, endDate, {headers: {'Content-Type': 'text/plain'}})
                .then(response => commit('updateSelectedUserDetail', response.data));
        }
        ,
        startProlongedSickLeaveForSelectedUser({commit, state}, startDate) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.post(`/administration/user/${userId}/onProlongedSickLeave/start`, startDate, {headers: {'Content-Type': 'text/plain'}})
                .then(response => commit('updateSelectedUserDetail', response.data));
        }
        ,
        updateProlongedSickLeaveForSelectedUser({commit, state}, updateProlongedSickLeaveDatesDto) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.put(`/administration/user/${userId}/onProlongedSickLeave/update`, updateProlongedSickLeaveDatesDto)
                .then(response => commit('updateSelectedUserDetail', response.data));
        }
        ,
        deleteProlongedSickLeaveForSelectedUser({commit, state}, prolongedSickLeaveId) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.delete(`/administration/user/${userId}/onProlongedSickLeave/${prolongedSickLeaveId}`)
                .then(response => commit('updateSelectedUserDetail', response.data));
        }
        ,
        terminateUserWithEndDate({commit, state}, terminationDate) {
            const config = {headers: {'Content-Type': 'text/plain'}};
            const userId = state.selectedUser.id;
            return defaultAxiosClient.put(`/administration/user/${userId}/terminate/`, terminationDate, config)
                .then(response => commit('updateSelectedUserDetail', response.data));
        }
        ,
        activateUser({commit, state}) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.put(`/administration/user/${userId}/activate`)
                .then(response => commit('updateSelectedUserDetail', response.data))
                .catch(() => commit('resetState'));
        }
        ,
        fetchRecupForYear({commit, state}) {
            const year = toYear(this.state.time.currentlySelectedMonth);
            const userId = state.selectedUser.id;
            return defaultAxiosClient.get(`/administration/recup/${year}/${userId}`)
                .then(response => {
                    const recupForMonthDtos = response.data.recupForMonthDtos;
                    const recupBonuses = response.data.recupBonusDtos;
                    commit('setRecupForYearDto', recupForMonthDtos);
                    commit('setRecupBonusDtos', recupBonuses);
                }).catch(() => commit('resetState'));
        }
        ,
        updateApprovedByAdmin({state, commit}, {userId, isApprovedByAdmin}) {
            const yearString = new Date(state.usedYear).getFullYear();
            return defaultAxiosClient.put(`/administration/user/${userId}/${yearString}/${isApprovedByAdmin}/updateApprovedByAdmin`).then(response => {
                commit('setVacationCounters', response.data);
                commit('setPreviousYearAvailable', response.data);
                commit('setNextYearAvailable', response.data);
            })
                .catch(() => commit('resetState'));
        }
        ,
        updatePartTimePercentage({state, commit}, updatePartTimePercentageDto) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.put(`/administration/user/${userId}/updatePartTimePercentage`, updatePartTimePercentageDto)
                .then(response => commit('updateSelectedUserDetail', response.data));
        }
        ,
        createPartTimePercentageRecord({state, commit}, createPartTimePercentageRecordDto) {
            const userId = state.selectedUser.id;
            return defaultAxiosClient.post(`/administration/user/${userId}/createNewPartTimePercentageRecord`, createPartTimePercentageRecordDto)
                .then(response => commit('updateSelectedUserDetail', response.data));
        }
    }
};
export default user;